import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Check, Pencil, Trash2, ImageIcon, Info } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ImageChangeDialog } from "@/components/ImageChangeDialog";
import { ExerciseDetailDialog } from "@/components/ExerciseDetailDialog";
import { getExerciseOptions } from "@/data/defaultRoutine";
import { useApp } from "@/context/AppContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

export function ExerciseCard({
  exercise,
  getProgress,
  onToggleSet,
  onUpdateWeight,
  onUpdateReps,
  onEdit,
  onDelete,
  onUpdateImage,
  selectedAlt = 0,
  onSelectAlt,
}) {
  const { startRestTimer } = useApp();
  const sets = Array.from({ length: exercise.sets }, (_, i) => i);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);

  const options = getExerciseOptions(exercise);
  const active = options ? options[selectedAlt] || options[0] : null;
  const displayName = active ? active.name : exercise.name;
  // Cada variante trae su propia animación, así que la imagen sigue a la
  // opción elegida y no al ejercicio base.
  const displayImg = (active && active.img) || exercise.img;
  const displayLibraryId = (active && active.libraryId) || exercise.libraryId;

  // Un fallo de carga es por imagen concreta, no por tarjeta.
  React.useEffect(() => setImgError(false), [displayImg]);

  const handleDelete = () => {
    onDelete();
    setConfirmDelete(false);
    toast.success("Ejercicio eliminado");
  };

  const handleToggle = (i) => {
    const becomingDone = !getProgress(i).done;
    onToggleSet(i);
    if (becomingDone) startRestTimer(90);
  };

  const overlayButton =
    "flex items-center justify-center h-7 w-7 rounded-lg transition-colors duration-200";
  const overlayStyle = {
    background: "rgba(15, 23, 42, 0.7)",
    backdropFilter: "blur(8px)",
    border: "1px solid rgba(255,255,255,0.1)",
  };

  return (
    <>
      <Card className="rounded-3xl overflow-hidden glass-surface border-0 fp-shadow">
        {/* Cabecera horizontal: animación a la izquierda, datos a la derecha */}
        <div className="p-4 flex gap-3">
          <div className="relative h-28 w-28 sm:h-32 sm:w-32 flex-shrink-0 rounded-2xl overflow-hidden bg-secondary/40">
            {imgError ? (
              <div className="w-full h-full bg-secondary flex items-center justify-center">
                <span className="text-muted-foreground text-[10px] text-center px-1">Sin imagen</span>
              </div>
            ) : (
              <img
                // key: al cambiar de variante hay que reintentar la carga, o un
                // fallo previo dejaría la nueva animación oculta para siempre.
                key={displayImg}
                src={displayImg}
                alt={displayName}
                // contain, no cover: los GIFs vienen con el cuerpo centrado y
                // recortarlos deja fuera justo el movimiento.
                className="w-full h-full object-contain"
                loading="lazy"
                onError={() => setImgError(true)}
              />
            )}

            <div className="absolute top-1.5 right-1.5 flex flex-col items-end gap-1.5">
              {displayLibraryId && (
                <button
                  onClick={() => setDetailOpen(true)}
                  aria-label="Ver instrucciones"
                  className={`${overlayButton} text-foreground/80 hover:text-foreground`}
                  style={overlayStyle}
                >
                  <Info className="h-3 w-3" />
                </button>
              )}
              <button
                onClick={() => setImageDialogOpen(true)}
                aria-label="Cambiar imagen"
                className={`${overlayButton} text-foreground/60 hover:text-foreground`}
                style={overlayStyle}
              >
                <ImageIcon className="h-3 w-3" />
              </button>
            </div>
          </div>

          <div className="flex-1 min-w-0 flex flex-col justify-center">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <Badge
                  variant="secondary"
                  className="text-[10px] font-semibold uppercase tracking-[0.1em] bg-primary/20 text-primary border-0 rounded-full mb-1.5 px-2 py-0.5"
                >
                  {exercise.muscleGroup}
                </Badge>
                <h3 className="text-foreground font-bold text-base sm:text-lg tracking-tight leading-tight line-clamp-2">
                  {displayName}
                </h3>
              </div>

              <div className="flex flex-col gap-1.5 flex-shrink-0">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onEdit}
                  aria-label="Editar ejercicio"
                  className="h-8 w-8 rounded-full bg-secondary/50 text-foreground hover:bg-secondary/80"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setConfirmDelete(true)}
                  aria-label="Eliminar ejercicio"
                  className="h-8 w-8 rounded-full bg-secondary/50 text-destructive hover:bg-secondary/80"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>

            <p className="text-muted-foreground text-xs font-medium mt-1">
              {exercise.sets} Series · Objetivo: {exercise.reps}
            </p>
          </div>
        </div>

        {options && options.length > 1 && (
          <div className="px-4 pb-1">
            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">
              Ejercicio de hoy
            </p>
            <div className="flex flex-wrap gap-1.5">
              {options.map((opt, idx) => (
                <button
                  key={idx}
                  onClick={() => onSelectAlt && onSelectAlt(idx)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors duration-200 ${
                    selectedAlt === idx
                      ? "bg-primary text-primary-foreground"
                      : "bg-secondary/50 text-muted-foreground hover:text-foreground hover:bg-secondary/80"
                  }`}
                >
                  {opt.name}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="px-4 pt-2.5 pb-1">
          <div className="flex items-center gap-3 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
            <div className="flex-1">Serie</div>
            <div className="w-[60px] text-center">Peso</div>
            <div className="w-[52px] text-center">Reps</div>
            <div className="w-10" />
          </div>
        </div>

        <div className="px-4 pb-3">
          {sets.map((i) => {
            const p = getProgress(i);
            const done = p.done;
            const inputClass = `h-8 text-center rounded-lg border-border/40 bg-secondary/30 text-xs font-semibold placeholder:text-muted-foreground/40 focus-visible:ring-primary/40 ${
              done ? "text-success border-transparent bg-transparent" : "text-foreground"
            }`;

            return (
              <div
                key={i}
                className={`flex items-center gap-3 py-2.5 border-b border-border/40 last:border-b-0 transition-opacity duration-200 ${
                  done ? "opacity-70" : ""
                }`}
              >
                <div className="flex-1 min-w-0">
                  <span
                    className={`text-sm font-medium ${
                      done ? "text-success line-through" : "text-foreground/90"
                    }`}
                  >
                    Serie {i + 1}
                  </span>
                  <span className="text-muted-foreground text-xs ml-1">({exercise.reps})</span>
                </div>

                <div className="flex items-center gap-1">
                  <Input
                    type="number"
                    placeholder="0"
                    value={p.weight || ""}
                    disabled={done}
                    onChange={(e) => {
                      const v = parseFloat(e.target.value.replace(",", "."));
                      onUpdateWeight(i, isNaN(v) ? 0 : v);
                    }}
                    className={`w-[60px] ${inputClass}`}
                  />
                  <span className="text-muted-foreground text-[10px] font-medium w-[14px]">kg</span>
                </div>

                <div className="flex items-center gap-1">
                  <Input
                    type="number"
                    placeholder="-"
                    value={p.reps || ""}
                    disabled={done}
                    onChange={(e) => {
                      const v = parseInt(e.target.value, 10);
                      if (onUpdateReps) onUpdateReps(i, isNaN(v) ? 0 : v);
                    }}
                    className={`w-[52px] ${inputClass}`}
                  />
                </div>

                <motion.button
                  whileTap={{ scale: 0.9 }}
                  onClick={() => handleToggle(i)}
                  aria-label={done ? `Desmarcar serie ${i + 1}` : `Completar serie ${i + 1}`}
                  className={`h-9 w-9 rounded-full flex items-center justify-center flex-shrink-0 transition-colors duration-200 ${
                    done
                      ? "bg-success text-success-foreground"
                      : "border border-muted-foreground/25 text-muted-foreground/30 hover:border-primary/50 hover:text-primary/50"
                  }`}
                >
                  <Check className="h-3.5 w-3.5" />
                </motion.button>
              </div>
            );
          })}
        </div>
      </Card>

      <ImageChangeDialog
        open={imageDialogOpen}
        onOpenChange={setImageDialogOpen}
        exerciseName={displayName}
        onSave={(newImg) => onUpdateImage && onUpdateImage(newImg)}
      />

      <ExerciseDetailDialog
        open={detailOpen}
        onOpenChange={setDetailOpen}
        libraryId={displayLibraryId}
      />

      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent className="glass-surface rounded-3xl border-0 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-foreground">Eliminar ejercicio</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              ¿Seguro que quieres eliminar "{displayName}"?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-row gap-2">
            <Button
              variant="ghost"
              onClick={() => setConfirmDelete(false)}
              className="flex-1 rounded-xl glass-surface text-foreground/80 hover:text-foreground hover:bg-secondary/60"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleDelete}
              className="flex-1 rounded-xl bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
